import React, { useEffect, useState } from 'react';

import { DatasetDataApi } from "api/DatasetDataApi";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { UpdateDatasetData } from "api/model/requests";
import { InputNumber } from 'primereact/inputnumber';
import { easasAlert } from 'components/fragments/easas-global-fragment/EasasGlobalAlerts';
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { T } from 'services/lang/LanguageService';
import { InputSwitch } from "primereact/inputswitch";
import { ChronoUnitLegacy, truncateDate } from "utils/time-utils";
import CalendarWrapper from "../../../basic/calendar-wrapper/CalendarWrapper";
import { ICombinedData, IDataSetConfig } from "../common/api/interfaces/interfaces";
import { getExtractedDataValue, remakeArrayIntoOtherInterface } from "../utils";
import './DataInputOutputUpdateTable.scss';

type DataInputOutputUpdateTableProps = {
    identifiers: Array<string>
    firstLoadDate: Date
    datasets: Array<IDataSetConfig>
    dateFrequency: ChronoUnitLegacy
    titleColumnReader(rowData: ICombinedData): JSX.Element | undefined
    closeDialog(): void
}

const DataInputOutputUpdateTable: React.FC<DataInputOutputUpdateTableProps> = ({
  identifiers,
  titleColumnReader,
  firstLoadDate,
  datasets,
  dateFrequency,
  closeDialog,
}) => {
  const [dataQuery, setDataQuery] = useState<Array<any>>([]);

  const [date, setDate] = useState<Date>(truncateDate(firstLoadDate || new Date(), dateFrequency));

  const fetchQuery = useGetDataInfoRange(
    identifiers,
    { timeStart: date.toISOString() || firstLoadDate.toISOString() },
    {
      enabled: date && identifiers.length > 0,
    }
  );

  useEffect(() => {
    if (
      fetchQuery.data &&
      !fetchQuery.isError &&
      !fetchQuery.isLoading &&
      fetchQuery.data?.length > 0
    ) {
      setDataQuery([...remakeArrayIntoOtherInterface(fetchQuery.data, datasets)]);
    }
  }, [fetchQuery.data]);

  useEffect(() => {
    fetchQuery.refetch();
  }, [date]);

  const updateData = () => {
    const tempArr: Array<UpdateDatasetData | undefined> = dataQuery
      .map(rowData => {
        const tempOb = fetchQuery.data?.find((ob: any) => ob.identifier === rowData.identifier);
        if (tempOb && rowData.data.length > 0 && tempOb.data !== rowData.data) {
          if (tempOb.data.length === 0 && !rowData.data[0].value) {
            return;
          }
          if (tempOb.data.length > 0 && tempOb.data[0].value === rowData.data[0].value) {
            return;
          }

          const objVal: { [k: string]: any } = {};

          let dateString = date.toISOString();
          if (rowData.data.length > 0) {
            dateString = rowData.data[0].key;
          }

          objVal[`${dateString}`] = rowData.data[0].value;

          return {
            identifier: rowData.identifier,
            values: objVal,
          };
        }
      })
      .filter(ob => ob != null);

    if (tempArr.length === 0) {
      easasAlert({ summary: T("alert.no-changes-made"), severity: "warn" });
      return;
    }
    DatasetDataApi.updateData(tempArr as Array<UpdateDatasetData>)
      .then(() => {
        easasAlert({ summary: T("alert.datasets-updated"), severity: "success" });
        closeDialog();
      })
      .catch(er => {
        console.error(er);
        easasAlert({ summary: T("alert.error-while-updating-datasets"), severity: "error" });
        closeDialog();
      });
  };

  const valueBodyInputTemplate = (rowData: ICombinedData) => {
    if (rowData.type.toUpperCase() === "NUMBER") {
      let suffix = "";
      let inputDisabled = false
      if (rowData.unit.length > 0 && rowData.unit !== " ") {
        suffix = ` ${rowData.unit}`;
      }
      for(let i = 0; i < datasets.length; i++) {
        if(datasets[i].identifier === rowData.identifier && datasets[i].editable === false) {
          inputDisabled = true          
        }
      }
      return (
        <InputNumber
          value={getExtractedDataValue(rowData.data, rowData.decimalPlaces)}
          onChange={e => {
            valueChangeHandler(rowData.identifier as string, e.value);
          }}
          maxFractionDigits={rowData.decimalPlaces}
          disabled={inputDisabled}
          suffix={suffix}
          placeholder={suffix}
        />
      );
    }
    if (rowData.type.toUpperCase() === "BOOLEAN") {
      return (
        <InputSwitch
          checked={getExtractedDataValue(rowData.data, 0) == 1}
          onChange={e => {
            valueChangeHandler(rowData.identifier as string, e.value ? 1 : 0);
          }}
        />
      );
    }

    if (rowData.type.toUpperCase() === "ENUM") {
      const enums = datasets.filter(e => e.identifier === rowData.identifier)[0].enum;

      if (enums) {
        const dropdownOptions = Object.entries(enums).map(([key, value]) => ({
          label: key,
          value,
        }));

        return (
          <Dropdown
            value={+getExtractedDataValue(rowData.data, 0)}
            options={dropdownOptions}
            onChange={e => {
              valueChangeHandler(rowData.identifier as string, e.value);
            }}
          />
        );
      }
      return T("message.no-enum-property");
    }

    return <>[unknown type]</>;
  };

  const valueChangeHandler = (identifier: string, newValue: any) => {
    const tempArr = dataQuery.map(item => {
      if (item.identifier === identifier) {
        return {
          ...item,
          data:
            item.data.length > 0
              ? [{ ...item.data[0], value: newValue }]
              : [{ key: `${date.toISOString()}`, value: newValue }],
        };
      }
      return item;
    });

    setDataQuery(tempArr);
  };

  const onCancelClickHandler = () => {
    closeDialog();
  };

  return (
    <>
      {fetchQuery.isError && <>{T("alert.error")}...</>}

      {!fetchQuery.isError && (
        <div className="full-dialog-main-window">
          <div>
            <CalendarWrapper value={date} onChange={setDate} dateFrequency={dateFrequency} />
          </div>

          <DataTable
            value={dataQuery}
            editMode="cell"
            className="editable-cells-table"
            responsiveLayout="scroll"
            scrollHeight="50vh"
            loading={fetchQuery.isLoading}
            size="small"
          >
            <Column
              field="title"
              header={T("word.dataset")}
              body={titleColumnReader}
              style={{ width: "90%" }}
            />
            <Column field="unit" header={T("word.value")} body={valueBodyInputTemplate} />
          </DataTable>
          <div className="button-container">
            <Button
              label={T("action.update")}
              icon="pi pi-pencil"
              onClick={() => updateData()}
              className="p-button-success p-button-rounded p-button-sm"
            />
            <Button
              label={T("word.cancel")}
              icon="pi pi-times"
              onClick={() => onCancelClickHandler()}
              className="p-button-danger p-button-rounded p-button-sm"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default DataInputOutputUpdateTable;